import { last } from "@prismicio/editor-support/Array"
import {
  EmbedBlock,
  ImageBlock,
  TextBlock,
} from "@prismicio/types-internal/lib/content"
import TTDocument from "@tiptap/extension-document"
import type { Node as ProsemirrorNode, Schema } from "prosemirror-model"

import { BulletList, OrderedList } from "../extensions"
import {
  type DocumentConverterReturnType,
  DocumentExtension,
  isTextExtension,
  type NodeExtension,
  NodeType,
} from "../models"
import { NodeUtils } from "../models/helpers/NodeUtils"
import {
  type ExtendedBlock,
  type ExtendedRichTextContent,
  ExtendedRichTextNodeType,
  isTableBlock,
} from "../types"
import { mergeLists } from "./ListItem"
import { prismicToProsemirrorMark } from "./Text"

const componentName = "document"

function component(multiLines: boolean) {
  return TTDocument.extend({
    name: componentName,
    content: multiLines ? "block+" : "block",
  })
}

function converter<TContent extends ExtendedRichTextContent>(
  extensions: NodeExtension[],
): DocumentConverterReturnType<TContent> {
  return {
    fromPrismic(schema, nodeTypes, richText) {
      const sections = richText.value.reduce<ProsemirrorNode[]>(
        (acc, rawBlock) => {
          const { extension, block } = getBestMatchingExtensionBlock(
            extensions,
            rawBlock,
            schema,
            nodeTypes,
          )
          const node = getNode({ extension, block, schema })
          const ancestors = acc.slice(0, acc.length - 1)
          const previousNode = last(acc)

          const processedNode = getProcessedNode({
            node,
            previousNode,
          })

          return processedNode.updatedNode
            ? [
                ...(processedNode.merged ? ancestors : acc),
                processedNode.updatedNode,
              ]
            : acc
        },
        [],
      )

      return schema.nodes[componentName]?.create({}, sections)
    },

    toPrismic(document) {
      const blocks = NodeUtils.children.reduce<readonly ExtendedBlock[]>(
        document,
        (acc, child: ProsemirrorNode) => {
          const extension = Object.values(extensions).find(
            (extension) => child.type.name === extension.name,
          )
          const converted = extension?.converter.toPrismic(child)
          if (!converted)
            console.warn(`Cannot convert node of type '${child.type.name}'.`)
          return converted
            ? [
                ...acc,
                ...(converted instanceof Array ? converted : [converted]),
              ]
            : acc
        },
        [],
      )
      return {
        __TYPE__: "StructuredTextContent",
        value: [...blocks],
      } as TContent
    },
  }
}

const EditorDocument = new DocumentExtension(
  componentName,
  component,
  converter,
)
// eslint-disable-next-line import/no-default-export
export default EditorDocument

interface getNodeProps {
  extension: NodeExtension | undefined
  block: ExtendedBlock
  schema: Schema
}

function getNode(props: getNodeProps) {
  const { extension, block, schema } = props

  if (!extension) return

  switch (extension.nodeType) {
    case NodeType.Embed:
      if (!EmbedBlock.is(block)) return
      return extension.converter.fromPrismic(schema)(block)

    case NodeType.Text:
      if (!TextBlock.is(block)) return
      return extension.converter.fromPrismic(schema)(block)

    case NodeType.Image:
      if (!ImageBlock.is(block)) return
      return extension.converter.fromPrismic(schema)(block)

    case NodeType.Table:
      if (!isTableBlock(block)) return
      return extension.converter.fromPrismic(schema)(block)
  }
}

interface getProcessedNodeProps {
  node: ProsemirrorNode | undefined
  previousNode: ProsemirrorNode | undefined
}

function getProcessedNode(props: getProcessedNodeProps) {
  const { node, previousNode } = props

  if (!node || !previousNode) return { merged: false, updatedNode: node }

  if (
    node.type.name === previousNode.type.name &&
    (node.type.name === BulletList.name || node.type.name === OrderedList.name)
  ) {
    return { merged: true, updatedNode: mergeLists(previousNode, node) }
  }

  return { merged: false, updatedNode: node }
}

function getBestMatchingExtensionBlock(
  extensions: NodeExtension[],
  block: ExtendedBlock,
  schema: Schema,
  nodeTypes: Set<ExtendedRichTextNodeType>,
) {
  // Ideally we find an extension that is both allowed by the config and understand the current content.
  const exactMatchExtension = extensions.find(
    (extension) =>
      extension.richTextNodeTypes.includes(block.type) &&
      nodeTypes.has(block.type),
  )

  if (exactMatchExtension) return { extension: exactMatchExtension, block }

  // Could not find a matching extension and the block is not text; we can't convert content here.
  // The block will just get dropped.
  // Note that this typecheck is broken in types-internal and doesn't narrow down the type right now.
  if (!TextBlock.is(block)) return { extension: undefined, block }

  // We convert from text to text.
  // Now we just get any text extension that this rich text has.
  // The goal is to keep the previous text so users can actually see and edit the previous value,
  // even if the node types are now different.
  const partialMatchExtension = extensions.find(
    (extension) => isTextExtension(extension) && extension.name in schema.nodes,
  )

  const filteredSpans = block.content.spans?.filter((span) => {
    const proseMark = prismicToProsemirrorMark[span.type]
    return proseMark && proseMark in schema.marks
  })

  return {
    extension: partialMatchExtension,
    block: {
      ...block,
      content: {
        text: block.content.text,
        spans: filteredSpans,
      },
    },
  }
}
